import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { FORMATE, ZIELE, type Op, baseVideoTrack, holBefund, ordnerName, sortedClips, timelineDuration, vorZeit } from '@schnitt/shared';
import { api } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';
import { FeedbackSheet } from './FeedbackSheet';
import { OrdnerWahl } from './OrdnerWahl';
import { AblageOrdnerSheet } from './AblageOrdnerSheet';

/**
 * Project settings: the name, the frame it is cut for, and getting a finished
 * project out of the way.
 */

// Die Formate stehen seit 7.24b im gemeinsamen Teil: Blatt und der Knopf an
// der Vorschau zeigen dieselbe Liste.
const FORMATS = FORMATE;

/**
 * h.264 cannot encode an odd width or height, and the renderer quietly drops
 * the last row rather than fail -- which would leave the preview and the
 * export a pixel apart. So a free format is even from the start.
 */
function evenSize(value: number): number {
  const clamped = Math.min(4096, Math.max(64, Math.round(value)));
  return clamped - (clamped % 2);
}

export function ProjectSettingsSheet({
  onClose,
  onLeave,
  onManageAccess,
  onVerlauf,
}: {
  onClose: () => void;
  onLeave: () => void;
  onManageAccess: () => void;
  onVerlauf: () => void;
}): ReactElement {
  const [feedback, setFeedback] = useState(false);
  /** Die Ablage des Projekts (7.50): welcher Ordner, und was darin liegt. */
  const [ablageWahl, setAblageWahl] = useState(false);
  const [ablage, setAblage] = useState<Awaited<ReturnType<typeof api.ablageStand>>['ablage']>(null);
  const [ablageBusy, setAblageBusy] = useState(false);
  /** Der Befund zum Auslagern (7.51) -- erst prüfen, dann entfernen. */
  const [auslagerBefund, setAuslagerBefund] = useState<
    Awaited<ReturnType<typeof api.auslagernPruefen>>['befund'] | null
  >(null);
  const project = useEditor((s) => s.project);
  const settings = useEditor((s) => s.timeline.settings);
  const timeline = useEditor((s) => s.timeline);
  const dispatch = useEditor((s) => s.dispatch);
  const refreshProject = useEditor((s) => s.refreshProject);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);
  const assets = useEditor((s) => s.assets);
  /**
   * Was nach dem Zurückholen wirklich dasteht (7.92) -- dieselbe Rechnung wie
   * im Prüfstand, aus dem gemeinsamen Teil. Gefragt ist nicht die Wolke,
   * sondern was *hier* liegt: ein Medium ohne `originalUrl` lässt sich weder
   * exportieren noch einfrieren.
   */
  const holStand = holBefund(
    assets.map((a) => ({ name: a.name, status: a.status, hatOriginal: Boolean(a.originalUrl), fehler: a.error })),
  );

  const [name, setName] = useState(project?.name ?? '');
  const [folder, setFolder] = useState(project?.folder ?? '');
  const [ordner, setOrdner] = useState<string[]>([]);
  const ordnerLaden = useCallback(
    async () => setOrdner((await api.ordner()).ordner.map((o) => o.name)),
    [],
  );
  useEffect(() => {
    void ordnerLaden().catch(() => setOrdner([]));
  }, [ordnerLaden]);
  const ablageLaden = useCallback(async () => {
    // Ohne verbundenen Ordner wird gar nicht erst gefragt: das Projekt sagt
    // selbst, ob es einen hat. Und antwortet die Ablage nicht, bleibt der
    // Abschnitt leer statt laut -- die Projekteinstellungen müssen auch ohne
    // Wolke aufgehen.
    if (!project?.ablage) {
      setAblage(null);
      return;
    }
    setAblage((await api.ablageStand(project.id).catch(() => ({ ablage: null }))).ablage);
  }, [project]);
  useEffect(() => {
    void ablageLaden();
  }, [ablageLaden]);
  // Der Wert kommt als Argument, nicht aus dem Zustand: nach einem setFolder
  // steht dort im selben Durchlauf noch der alte, und gespeichert wuerde der
  // vorletzte Ordner.
  const saveFolder = async (gewaehlt: string) => {
    const neu = ordnerName(gewaehlt);
    if (!project || neu === ordnerName(project.folder ?? '')) return;
    try {
      await api.setProjectFolder(project.id, neu);
      await refreshProject();
      void ordnerLaden();
      toast('success', neu ? `Liegt jetzt im Ordner „${neu}“.` : 'Aus dem Ordner genommen.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Ordner konnte nicht gespeichert werden.');
    }
  };
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Die Notizen (S26). Der Anfangswert kommt vom Projekt; wer weitertippt,
  // soll nicht beim nächsten Nachladen überschrieben werden -- deshalb ein
  // eigener Stand und keine Ableitung.
  const [notizen, setNotizen] = useState(project?.notizen ?? '');
  const [notizenStand, setNotizenStand] = useState<'gespeichert' | 'fehler' | null>(null);
  const [ownWidth, setOwnWidth] = useState(String(settings.width));
  const [ownHeight, setOwnHeight] = useState(String(settings.height));

  const isOwner = role === 'owner';
  const duration = timelineDuration(timeline);

  const rename = async () => {
    if (!project || name.trim() === project.name) return;
    setBusy(true);
    try {
      await api.renameProject(project.id, name.trim());
      await refreshProject();
      toast('success', 'Umbenannt.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Umbenennen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const setFormat = (width: number, height: number) => {
    // A frame change is an ordinary timeline operation, so it travels to
    // everyone else in the project and undo takes it back.
    dispatch({ op: 'updateSettings', patch: { width, height } } as Op);
  };

  const applyOwnFormat = () => {
    const width = Number(ownWidth);
    const height = Number(ownHeight);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 64 || height < 64) {
      toast('error', 'Breite und Höhe zwischen 64 und 4096 Pixeln.');
      return;
    }
    const [w, h] = [evenSize(width), evenSize(height)];
    setOwnWidth(String(w));
    setOwnHeight(String(h));
    setFormat(w, h);
    toast('success', `Bildformat ${w}×${h}.`);
  };

  /** The first clip on the base track, and the size of the file behind it. */
  const firstClipSize = (() => {
    const track = baseVideoTrack(timeline);
    const clip = track ? sortedClips(track)[0] : undefined;
    if (!clip || clip.source.kind !== 'asset') return null;
    const assetId = clip.source.assetId;
    const asset = assets.find((a) => a.id === assetId);
    if (!asset || asset.width < 64 || asset.height < 64) return null;
    return { width: evenSize(asset.width), height: evenSize(asset.height) };
  })();

  const setFps = (fps: number) => dispatch({ op: 'updateSettings', patch: { fps } } as Op);
  const setZielLautheit = (zielLautheit: number | null) =>
    dispatch({ op: 'updateSettings', patch: { zielLautheit } } as Op);

  /**
   * Notizen (S26). Der Text steht im Feld, gespeichert wird beim Verlassen --
   * bei jedem Tastendruck zu schreiben hiesse, für einen Merkzettel eine
   * Anfrage je Buchstabe zu stellen.
   */
  const notizenSichern = async () => {
    if (!project || notizen === (project.notizen ?? '')) return;
    try {
      await api.notizenSpeichern(project.id, notizen);
      await refreshProject();
      setNotizenStand('gespeichert');
    } catch {
      setNotizenStand('fehler');
    }
  };

  const archive = async () => {
    if (!project) return;
    setBusy(true);
    try {
      await api.setProjectArchived(project.id, true);
      toast('info', 'Archiviert. In der Übersicht unter „Archiv anzeigen“.');
      onLeave();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Archivieren fehlgeschlagen.');
      setBusy(false);
    }
  };

  const entferne = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const { name } = await api.deleteProject(project.id);
      toast('info', `„${name}“ ist gelöscht.`);
      onLeave();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!project) return;
    setBusy(true);
    try {
      await api.setProjectArchived(project.id, false);
      await refreshProject();
      toast('success', 'Wieder aktiv.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Zurückholen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const active = FORMATS.find((f) => f.width === settings.width && f.height === settings.height);

  return (
    <Sheet title="Projekt" onClose={onClose}>
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => void rename()} readOnly={!isOwner} />
      </label>
      {!isOwner ? <p className="hint" style={{ marginTop: -6 }}>Nur die Eigentümerin kann das Projekt ändern.</p> : null}
      <div className="field">
        <span>Ordner auf der Startseite</span>
        {isOwner ? (
          <OrdnerWahl
            wert={folder}
            vorhandene={ordner}
            onWahl={(neu) => {
              setFolder(neu);
              void saveFolder(neu);
            }}
          />
        ) : (
          <p className="hint" style={{ marginTop: 0 }}>{folder || 'Ohne Ordner'}</p>
        )}
      </div>

      {/* Der Ordner in der Ablage (7.50). Er steht hier und nicht in der
          Verwaltung, weil er zu *diesem* Projekt gehört -- die Zugangsdaten
          gelten für den ganzen Betrieb, der Ordner für ein Projekt. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Ordner in der Ablage</h3>
      {ablage ? (
        <div className="list" data-ablage-verbunden={ablage.ordner}>
          <div className="row" style={{ textAlign: 'left', display: 'block' }}>
            <strong style={{ display: 'block' }}>{ablage.ordner || '/'}</strong>
            <span className="hint" style={{ margin: 0 }} data-ablage-auskunft>
              {ablage.anbieter === 'nextcloud' ? 'Nextcloud' : 'Box'} ·{' '}
              {/* Drei Fälle, und alle drei müssen etwas sagen (7.112): die
                  Ablage antwortet nicht, für den Anbieter sind gar keine
                  Zugangsdaten mehr eingetragen, oder es liegt etwas da. Der
                  mittlere fehlte -- dann stand hier `dateien.length` auf einem
                  `undefined`, und das ganze Blatt kam nicht. Wer seine
                  Zugangsdaten entfernt, soll lesen, was ist, und nicht auf ein
                  leeres Blatt sehen. */}
              {ablage.fehler
                ? ablage.fehler
                : ablage.eingerichtet === false
                  ? (ablage.hinweis ?? 'Für diese Ablage sind keine Zugangsdaten eingetragen.')
                  : `${(ablage.dateien ?? []).length} Datei${(ablage.dateien ?? []).length === 1 ? '' : 'en'} in „rohdaten“${
                      (ablage.neu ?? 0) > 0 ? `, davon ${ablage.neu} neu` : ''
                    }`}
            </span>
          </div>
          {/* Ist das Projekt gesichert? (7.222; Sascha, 4. Oktober: „sobald
              der ordner dann verbunden ist, sollte sich das projekt dort immer
              sichern“) -- gelesen aus dem Ordner selbst, nicht aus dem, was
              die App zu wissen glaubt. */}
          {ablage.sicherung ? (
            <div
              className="row"
              style={{ textAlign: 'left', display: 'block' }}
              data-ablage-sicherung={`${ablage.sicherung.oben}/${ablage.sicherung.medien}`}
            >
              <strong style={{ display: 'block' }}>
                {ablage.sicherung.schnittdatei
                  ? `Gesichert ${vorZeit(ablage.sicherung.schnittdatei)}`
                  : 'Der Schnitt liegt noch nicht im Ordner'}
              </strong>
              <span className="hint" style={{ margin: 0 }}>
                {ablage.sicherung.oben} von {ablage.sicherung.medien} Medien ·{' '}
                {ablage.sicherung.exporte === 1 ? '1 Export' : `${ablage.sicherung.exporte} Exporte`}
                {ablage.sicherung.fcpxml ? ' · auch für Final Cut' : ''}
                {ablage.sicherung.fehlen.length > 0
                  ? ` · noch nicht oben: ${ablage.sicherung.fehlen.join(', ')}${
                      ablage.sicherung.medien - ablage.sicherung.oben > ablage.sicherung.fehlen.length ? ' …' : ''
                    }`
                  : ''}
              </span>
            </div>
          ) : null}
          {/* Was nach dem Zurückholen wirklich dasteht (7.92). „Zurückholen“
              stellt nur die Aufträge; kommt einer nicht durch, stand bisher
              nirgends etwas -- die App meldete Erfolg, und das Medium war
              trotzdem leer. Jetzt steht es hier, mit dem Grund vom Medium. */}
          {!project?.ausgelagertAm && holStand.ohneDatei.length > 0 ? (
            <div className="row" style={{ textAlign: 'left', display: 'block' }} data-holfehler={holStand.ohneDatei.length}>
              <strong style={{ display: 'block' }}>Nicht alles ist zurückgekommen</strong>
              <span className="hint" style={{ margin: 0 }}>{holStand.satz}</span>
            </div>
          ) : null}
          {isOwner ? (
            <>
              {/* Platz freigeben (7.51). Erst prüfen, dann entfernen -- und
                  der Befund steht vorher da, nicht als Überraschung danach. */}
              {project?.ausgelagertAm ? (
                <button
                  className="row"
                  style={{ textAlign: 'left' }}
                  data-zurueckholen
                  disabled={ablageBusy}
                  onClick={async () => {
                    setAblageBusy(true);
                    try {
                      const { geholt, fehlen } = await api.zurueckholen(project!.id);
                      await refreshProject();
                      await ablageLaden();
                      toast(
                        'success',
                        fehlen.length > 0
                          ? `${geholt} Datei(en) werden geholt; ${fehlen.length} liegen nicht mehr oben.`
                          : `${geholt} Datei(en) werden geholt und wieder aufbereitet.`,
                      );
                    } catch (err) {
                      toast('error', err instanceof Error ? err.message : 'Ging nicht.');
                    } finally {
                      setAblageBusy(false);
                    }
                  }}
                >
                  <strong style={{ display: 'block' }}>Rohmaterial zurückholen</strong>
                  <span className="hint" style={{ margin: 0 }}>
                    Ausgelagert seit {new Date(project.ausgelagertAm).toLocaleDateString('de-DE')} — die Originale liegen
                    nur in der Ablage.
                  </span>
                </button>
              ) : (
                <button
                  className="row"
                  style={{ textAlign: 'left' }}
                  data-auslagern
                  disabled={ablageBusy}
                  onClick={async () => {
                    setAblageBusy(true);
                    try {
                      const { befund } = await api.auslagernPruefen(project!.id);
                      setAuslagerBefund(befund);
                      if (!befund.sicher) toast('info', befund.satz);
                    } catch (err) {
                      toast('error', err instanceof Error ? err.message : 'Prüfen ging nicht.');
                    } finally {
                      setAblageBusy(false);
                    }
                  }}
                >
                  <strong style={{ display: 'block' }}>Platz freigeben…</strong>
                  <span className="hint" style={{ margin: 0 }}>
                    Prüft erst, ob jede Datei oben liegt und dasselbe ist — entfernt wird nichts, bevor das feststeht.
                  </span>
                </button>
              )}
              {auslagerBefund ? (
                <div className="row" style={{ textAlign: 'left', display: 'block' }} data-auslager-befund={auslagerBefund.sicher ? 'sicher' : 'nein'}>
                  <span className="hint" style={{ margin: 0 }}>{auslagerBefund.satz}</span>
                  {auslagerBefund.sicher ? (
                    <button
                      className="primary"
                      style={{ marginTop: 8 }}
                      disabled={ablageBusy}
                      data-auslagern-los
                      onClick={async () => {
                        setAblageBusy(true);
                        try {
                          const { entfernt, platz } = await api.auslagern(project!.id);
                          setAuslagerBefund(null);
                          await refreshProject();
                          await ablageLaden();
                          toast('success', `${entfernt} Original(e) entfernt, ${Math.round(platz / 1024 / 1024)} MB frei.`);
                        } catch (err) {
                          toast('error', err instanceof Error ? err.message : 'Ging nicht.');
                        } finally {
                          setAblageBusy(false);
                        }
                      }}
                    >
                      {Math.round(auslagerBefund.platz / 1024 / 1024)} MB freigeben
                    </button>
                  ) : null}
                </div>
              ) : null}
              <button
                className="row"
                style={{ textAlign: 'left' }}
                data-ablage-schnitt
                disabled={ablageBusy}
                onClick={async () => {
                  setAblageBusy(true);
                  try {
                    const { datei, rev } = await api.ablageSchnittSchreiben(project!.id);
                    toast('success', `„${datei}“ liegt im Projektordner (Fassung ${rev}).`);
                    await ablageLaden();
                  } catch (err) {
                    toast('error', err instanceof Error ? err.message : 'Schreiben ging nicht.');
                  } finally {
                    setAblageBusy(false);
                  }
                }}
              >
                Schnitt jetzt in den Ordner schreiben
              </button>
              <button
                className="row"
                style={{ textAlign: 'left' }}
                data-ablage-loesen
                disabled={ablageBusy}
                onClick={async () => {
                  setAblageBusy(true);
                  try {
                    await api.ablageLoesen(project!.id);
                    await refreshProject();
                    await ablageLaden();
                    toast('info', 'Die Verbindung ist gelöst. In der Ablage bleibt alles liegen.');
                  } catch (err) {
                    toast('error', err instanceof Error ? err.message : 'Ging nicht.');
                  } finally {
                    setAblageBusy(false);
                  }
                }}
              >
                Verbindung lösen
              </button>
            </>
          ) : null}
        </div>
      ) : (
        <>
          <p className="hint" style={{ marginTop: 0 }}>
            Dieses Projekt hängt an keinem Ordner. Verbunden bekommt es einen Unterordner <em>rohdaten</em> für das
            Material, und der Schnitt liegt als Datei daneben — wer den Ordner kopiert, hat das Projekt.
          </p>
          {isOwner ? (
            <button className="row" style={{ textAlign: 'left' }} data-ablage-waehlen onClick={() => setAblageWahl(true)}>
              Ordner aussuchen…
            </button>
          ) : null}
        </>
      )}
      {ablageWahl && project ? (
        <AblageOrdnerSheet
          projectId={project.id}
          onClose={() => setAblageWahl(false)}
          onFertig={async () => {
            setAblageWahl(false);
            await refreshProject();
            await ablageLaden();
          }}
        />
      ) : null}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Bildformat</h3>
      <div className="list">
        {FORMATS.map((format) => {
          const selected = format.width === settings.width && format.height === settings.height;
          return (
            <button
              key={format.id}
              className="row"
              style={{
                textAlign: 'left',
                minHeight: 0,
                borderColor: selected ? 'var(--accent)' : 'var(--line)',
              }}
              onClick={() => setFormat(format.width, format.height)}
              disabled={role === 'viewer'}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  flex: '0 0 auto',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <div
                  style={{
                    width: format.width >= format.height ? 28 : (28 * format.width) / format.height,
                    height: format.width >= format.height ? (28 * format.height) / format.width : 28,
                    background: selected ? 'var(--accent)' : 'var(--surface-3)',
                    borderRadius: 3,
                  }}
                />
              </div>
              <div className="grow">
                <div className="title">{format.label}</div>
                <div className="sub">
                  {format.hint} · {format.width}×{format.height}
                </div>
              </div>
              {selected ? <span aria-hidden="true">●</span> : null}
            </button>
          );
        })}
      </div>
      {!active ? (
        <p className="hint" style={{ marginBottom: 0 }}>
          Zurzeit ein eigenes Format: {settings.width}×{settings.height}
        </p>
      ) : null}

      {/* Not every video is one of the four. A talking head filmed on a
          camcorder, a screen recording, an old 4:3 archive tape -- those need
          their own frame, or everything gets bars. */}
      <div className="row" style={{ gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
        <label className="field grow" style={{ margin: 0 }}>
          <span>Breite</span>
          <input
            type="number"
            inputMode="numeric"
            min={64}
            max={4096}
            step={2}
            value={ownWidth}
            onChange={(e) => setOwnWidth(e.target.value)}
            readOnly={role === 'viewer'}
          />
        </label>
        <label className="field grow" style={{ margin: 0 }}>
          <span>Höhe</span>
          <input
            type="number"
            inputMode="numeric"
            min={64}
            max={4096}
            step={2}
            value={ownHeight}
            onChange={(e) => setOwnHeight(e.target.value)}
            readOnly={role === 'viewer'}
          />
        </label>
        <button className="small" onClick={applyOwnFormat} disabled={role === 'viewer'}>
          Übernehmen
        </button>
      </div>
      {firstClipSize ? (
        <button
          className="small"
          style={{ width: '100%', marginTop: 8 }}
          onClick={() => {
            setOwnWidth(String(firstClipSize.width));
            setOwnHeight(String(firstClipSize.height));
            setFormat(firstClipSize.width, firstClipSize.height);
            toast('success', `Bildformat vom ersten Clip: ${firstClipSize.width}×${firstClipSize.height}.`);
          }}
          disabled={role === 'viewer'}
        >
          Format vom ersten Clip übernehmen ({firstClipSize.width}×{firstClipSize.height})
        </button>
      ) : null}
      {duration > 0 ? (
        <p className="hint">
          Das Format ändert nur den Rahmen. Einblendungen bleiben an ihrer Stelle im Bild.
        </p>
      ) : null}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Bildrate</h3>
      <div className="chips">
        {[24, 25, 30, 50, 60].map((fps) => (
          <button
            key={fps}
            className={`chip ${settings.fps === fps ? 'active' : ''}`}
            onClick={() => setFps(fps)}
            disabled={role === 'viewer'}
          >
            {fps} fps
          </button>
        ))}
      </div>

      {/* Notizen zum Projekt (S26). Sascha: „Ein Feld für das, was man sich
          merken muss.“ Am Projekt und nicht am Konto: die Freigabe des
          Interviews, der Name, der richtig geschrieben werden muss, die
          Stelle, die noch nachgedreht wird -- das betrifft alle, die daran
          arbeiten. Gespeichert wird beim Verlassen des Feldes, wie beim
          Projektnamen darüber. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Notizen</h3>
      <textarea
        data-notizen
        value={notizen}
        placeholder="Was man sich zu diesem Schnitt merken muss."
        rows={4}
        style={{ width: '100%', resize: 'vertical', minHeight: 88 }}
        readOnly={role === 'viewer'}
        onChange={(e) => setNotizen(e.target.value)}
        onBlur={() => void notizenSichern()}
      />
      {notizenStand ? (
        <p className="hint" data-notizen-stand={notizenStand}>
          {notizenStand === 'gespeichert' ? 'Gespeichert.' : 'Konnte nicht gespeichert werden.'}
        </p>
      ) : (
        <p className="hint">Für alle sichtbar, die am Projekt arbeiten. Nicht im Verlauf — eine Notiz ist kein Schnitt.</p>
      )}

      {/* **Magnetische Zeitleiste** (7.201; Sascha, 4. Oktober: „Bitte eine
          magnetische zeitachse bauen und aktivieren“). Eine Einstellung des
          Schnitts, nicht des Geräts: alle, die daran arbeiten, schneiden
          gleich. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Zeitleiste</h3>
      <div className="chips" data-magnet={settings.magnetisch ? 'an' : 'aus'}>
        <button
          className={`chip ${settings.magnetisch ? 'active' : ''}`}
          data-magnet-wahl="an"
          disabled={role === 'viewer'}
          onClick={() => {
            if (settings.magnetisch) return;
            dispatch({ op: 'updateSettings', patch: { magnetisch: true } } as Op);
            toast('success', 'Magnetisch: die Hauptspur hat keine Lücken mehr, und was darüber liegt, wandert mit.');
          }}
        >
          🧲 Magnetisch
        </button>
        <button
          className={`chip ${!settings.magnetisch ? 'active' : ''}`}
          data-magnet-wahl="aus"
          disabled={role === 'viewer'}
          onClick={() => {
            if (settings.magnetisch) dispatch({ op: 'updateSettings', patch: { magnetisch: false } } as Op);
          }}
        >
          Frei
        </button>
      </div>
      <p className="hint">
        {settings.magnetisch
          ? 'Die Hauptspur hat keine Lücken: Löschen, Kürzen und Verschieben ziehen den Rest zusammen. Titel, B-Roll und Ton über einem Clip wandern mit ihm.'
          : 'Jeder Clip bleibt, wo er liegt; Lücken auf der Hauptspur sind erlaubt.'}
      </p>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Lautheit</h3>
      <div className="chips" data-ziel-lautheit={String(settings.zielLautheit)}>
        <button
          className={`chip ${settings.zielLautheit === null ? 'active' : ''}`}
          onClick={() => setZielLautheit(null)}
          disabled={role === 'viewer'}
        >
          So lassen
        </button>
        {ZIELE.map((ziel) => (
          <button
            key={ziel.lufs}
            className={`chip ${settings.zielLautheit === ziel.lufs ? 'active' : ''}`}
            onClick={() => setZielLautheit(ziel.lufs)}
            disabled={role === 'viewer'}
          >
            {ziel.name.split(' (')[0]} ({ziel.lufs} LUFS)
          </button>
        ))}
      </div>
      <p className="hint">
        {settings.zielLautheit === null
          ? 'Der Export geht so hinaus, wie er gemischt ist. Wie laut das geworden ist, steht nach dem Rechnen beim Export.'
          : `Jeder Export wird auf ${settings.zielLautheit} LUFS gebracht, die Spitze unter −1 dBTP. Nachgemessen wird trotzdem.`}
      </p>
      {/* Neue Clips gleich laut (7.210; Sascha: „Auto Normalisierung“). */}
      <div className="chips" data-auto-normalisieren={settings.autoNormalisieren === false ? 'aus' : 'an'} style={{ marginTop: 10 }}>
        <button
          className={`chip ${settings.autoNormalisieren !== false ? 'active' : ''}`}
          data-auto-normalisieren-wahl="an"
          disabled={role === 'viewer'}
          onClick={() => {
            if (settings.autoNormalisieren === false) dispatch({ op: 'updateSettings', patch: { autoNormalisieren: true } } as Op);
          }}
        >
          Neue Clips angleichen
        </button>
        <button
          className={`chip ${settings.autoNormalisieren === false ? 'active' : ''}`}
          data-auto-normalisieren-wahl="aus"
          disabled={role === 'viewer'}
          onClick={() => {
            if (settings.autoNormalisieren !== false) dispatch({ op: 'updateSettings', patch: { autoNormalisieren: false } } as Op);
          }}
        >
          Pegel lassen
        </button>
      </div>
      <p className="hint">
        {settings.autoNormalisieren === false
          ? 'Neue Clips kommen mit dem Pegel ihrer Datei.'
          : 'Jeder neue Clip mit Ton wird beim Einfügen so laut wie mit „Normalisieren“. Einen Pegel, den du eingestellt hast, fasst das nicht an.'}
      </p>

      {/* Nur an einem Projekt, das wirklich in Box liegt (7.159). Vorher
          stand der Abschnitt an jedem -- mit leerer Nummer und einem
          „Öffnen“, das auf eine Box-Adresse ohne Ordner zeigte. */}
      {project?.boxFolderId ? (
      <>
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Box-Ordner</h3>
      <div className="row">
        <div className="grow">
          <div className="title" style={{ fontFamily: 'ui-monospace, monospace' }}>{project?.boxFolderId}</div>
          <div className="sub">Rohmaterial, Arbeitsdatei und Exporte liegen hier.</div>
        </div>
        <a className="chip" href={`https://app.box.com/folder/${project?.boxFolderId}`} target="_blank" rel="noreferrer">
          Öffnen
        </a>
      </div>
      {project?.boxFolderId ? (
        <div className="row" style={{ marginTop: 8 }}>
          <div className="grow">
            <div className="title">{project.boxWebhookId ? 'Box meldet neues Material von selbst' : 'Neues Material per „Box“ in der Medienliste'}</div>
            <div className="sub">
              {project.boxWebhookId
                ? 'Ein Webhook auf dem Ordner ist eingerichtet.'
                : 'Neue Dateien tauchen ohne Zutun auf.'}
            </div>
          </div>
          <button
            className="small"
            disabled={busy || !isOwner}
            onClick={() =>
              void (async () => {
                if (!project) return;
                setBusy(true);
                try {
                  if (project.boxWebhookId) {
                    await api.removeBoxWebhook(project.id);
                    toast('info', 'Webhook entfernt.');
                  } else {
                    const { created } = await api.ensureBoxWebhook(project.id);
                    toast('success', created ? 'Webhook eingerichtet.' : 'Webhook war schon da.');
                  }
                  await refreshProject();
                } catch (err) {
                  toast('error', err instanceof Error ? err.message : 'Das hat nicht geklappt.');
                } finally {
                  setBusy(false);
                }
              })()
            }
          >
            {project.boxWebhookId ? 'Entfernen' : 'Einrichten'}
          </button>
        </div>
      ) : null}
      </>
      ) : null}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Team und Externe</h3>
      <button style={{ width: '100%' }} onClick={onManageAccess} data-projekt-teilen>
        Zusammenarbeiten
      </button>

      {/* Der Verlauf: wer hat wann was geändert. Er gehört hierher und nicht
          in die Kopfleiste -- man sieht ihn selten, und wenn, dann bewusst. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Verlauf</h3>
      <button className="row" data-verlauf-oeffnen onClick={onVerlauf}>
        <div className="grow">
          <div className="title">Wer hat wann was geändert</div>
          <div className="sub">Jede Änderung mit Fassung und Person.</div>
        </div>
      </button>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Archiv</h3>
      {project?.archived ? (
        <>
          <div className="row">
            <div className="grow">
              <div className="title">Dieses Projekt ist archiviert</div>
              <div className="sub">Es taucht in der Übersicht nur unter „Archiv“ auf.</div>
            </div>
            <button className="small primary" onClick={() => void restore()} disabled={busy || !isOwner}>
              Zurückholen
            </button>
          </div>
          {/* Endgültig löschen (7.102). Erst hier, am archivierten Projekt:
              Archivieren ist umkehrbar, Löschen nicht, und ein einzelner
              Fehlgriff soll nicht genügen. Bis zum 18. September gab es den
              Weg gar nicht -- wer sich verklickte, hatte das Projekt für
              immer. */}
          {confirmDelete ? (
            <div className="card" style={{ margin: '10px 0 0' }}>
              <p style={{ marginTop: 0 }}>
                Löscht <b>{project.name}</b> mit allem, was daran hängt: Medien, Exporte, Hinweise, Gastlinks.
                Das lässt sich nicht zurückholen.
              </p>
              <div className="stack">
                <button className="primary" onClick={() => void entferne()} disabled={busy} data-projekt-loeschen-ja>
                  Ja, endgültig löschen
                </button>
                <button className="ghost" onClick={() => setConfirmDelete(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button
              style={{ width: '100%', marginTop: 10 }}
              onClick={() => setConfirmDelete(true)}
              disabled={!isOwner}
              data-projekt-loeschen
            >
              Endgültig löschen
            </button>
          )}
        </>
      ) : confirmArchive ? (
        <div className="card" style={{ margin: 0 }}>
          <p style={{ marginTop: 0 }}>
            Blendet das Projekt aus. Nichts wird gelöscht, du kannst es zurückholen.
          </p>
          <div className="stack">
            <button className="primary" onClick={() => void archive()} disabled={busy}>
              Ja, archivieren
            </button>
            <button className="ghost" onClick={() => setConfirmArchive(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <button style={{ width: '100%' }} onClick={() => setConfirmArchive(true)} disabled={!isOwner}>
          Projekt archivieren
        </button>
      )}

      {/* Melden, ohne den Schnittplatz zu verlassen: der Bereich ist schon
          ausgefüllt, weil der Knopf weiß, wo er steht. */}
      <button style={{ width: '100%', marginTop: 10 }} onClick={() => setFeedback(true)}>
        Etwas melden
      </button>
      {feedback ? <FeedbackSheet bereich="Schnittplatz" onClose={() => setFeedback(false)} /> : null}
    </Sheet>
  );
}
